<template>
  <modal
    name="onboarding"
    :adaptive="true"
    :click-to-close="false"
    height="auto"
    width="90%"
    :pivot-y="0.3"
    :scrollable="true"
    :max-width="1000"
  >
    <div v-if="errors.length" class="error-wrap">
      <Error
        :errors="errors"
        :error-action="submitMutation"
        error-action-label="Retry?"
        class="error"
      />
    </div>

    <keep-alive>
      <component
        :is="currentStepComponent"
        v-show="!errors.length"
        :selected-industry="selectedIndustry"
        :primary-color="primaryColor"
        :secondary-color="secondaryColor"
        :logo-image-url="logoImageUrl"
        :contact-email="contactEmail"
        :current-step="currentStep"
        @next-step="handleNextStep"
        @prev-step="handlePrevStep"
        @industry-selected="onIndustrySelected"
        @color-selected="onColorSelected"
        @contact-email-updated="onContactEmailUpdated"
        @uploading-complete="onUploadingComplete"
        @complete-onboarding="onCompleteOnboarding"
      />
    </keep-alive>
  </modal>
</template>

<script>
import Step3 from "./Step3";
import Step4 from "./Step4";
import Error from "@/components/UI/Error";
import { OnBoarding } from "@/graphql/mutations";
import { Account } from "@/graphql/queries";

import { getAccountId } from "@/lib/auth";

const defaultBrand = {
  primaryColor: "#6574cd",
  secondaryColor: "#F2AC79",
  logoImageUrl: "",
};

export default {
  name: "Onboarding",
  components: { Error, Step3, Step4 },
  props: {
    brands: {
      default: () => [],
      type: Array,
    },
    userEmail: {
      default: "",
      type: String,
    },
  },
  data() {
    return {
      currentStep: 0,
      errorCount: 0,
      errors: [],
      steps: ["Step3", "Step4"],
      stepLabels: ["", "", ""],
      selectedIndustry: "",
      primaryColor: this.getBrand("primaryColor"),
      secondaryColor: this.getBrand("secondaryColor"),
      primaryColorChanged: false,
      secondaryColorChanged: false,
      contactEmail: this.userEmail,
      logoImageUrl: this.getBrand("logoImageUrl"),
    };
  },
  computed: {
    currentStepComponent() {
      return this.steps[this.currentStep];
    },
    lastStep() {
      return this.steps.length === this.currentStep + 1;
    },
  },
  mounted() {
    this.$modal.show("onboarding");

    this.$gtm.trackEvent({
      event: "promo-on-boarding-started",
      accountId: getAccountId(),
    });
  },
  metaInfo: {
    bodyAttrs: {
      class: "background-cover",
    },
  },
  methods: {
    getBrand(prop) {
      // Check to see if got a brand from the account...
      const accountBrand = this.brands.length
        ? this.brands[[this.brands.length - 1]]
        : {};

      // Merge the default brand and the account's brand...
      const brand = {
        ...defaultBrand,
        ...accountBrand,
      };

      // Return the correct prop...
      return brand[prop];
    },
    onColorSelected(color) {
      this[color.item] = color.value;
      this[`${color.item}Changed`] = true;
    },
    onCompleteOnboarding() {
      this.submitMutation();
    },
    onContactEmailUpdated(email) {
      this.contactEmail = email;
    },
    handleNextStep() {
      this.currentStep++;
      this.$el.scrollTo(0, 0);
    },
    handlePrevStep() {
      this.currentStep--;
    },
    onIndustrySelected(indsutry) {
      this.selectedIndustry = indsutry;
    },
    onUploadingComplete(url) {
      this.logoImageUrl = url;
    },
    resetErrors() {
      this.errors = [];
    },
    submitMutation() {
      const {
        contactEmail,
        selectedTheme,
        logoImageUrl,
        primaryColor,
        secondaryColor,
        selectedIndustry,
      } = this;

      this.resetErrors();

      return this.$apollo
        .mutate({
          mutation: OnBoarding,
          variables: {
            input: {
              email: contactEmail,
              logoImageUrl: logoImageUrl,
              primaryColor: primaryColor,
              secondaryColor: secondaryColor,
              industry: selectedIndustry,
            },
          },
          update: (store) => {
            // Read the data from our cache for this query.
            const data = store.readQuery({
              query: Account,
            });

            // Update the cache with the response
            data.account.onboardingComplete = true;

            // Write our data back to the cache.
            store.writeQuery({
              query: Account,
              data,
            });
          },
        })
        .then((data) => {
          this.$gtm.trackEvent({
            event: "promo-on-boarding-complete",
            logoUploaded: this.logoImageUrl !== "",
            primaryColorChanged: this.primaryColorChanged,
            secondaryColorChanged: this.secondaryColorChanged,
            industryId: this.selectedIndustry,
          });

          this.$router.push("/templates");

          this.$modal.hide("onboarding");
        })
        .catch((err) => {
          // Set the error text...
          let errorText =
            "<p class='mb-0'>We're really sorry but it looks like there's been an error with our servers.</p>";

          // If the user has continued problems then offer a reason to report...
          if (this.errorCount) {
            errorText += `
              <p>If the problem persists please <a href="mailto:support@promo.ai?subject=Promo.ai Onboarding Error">email us</a> and a member of our team will get back to you.</p>
              <p class="mb-2">💸💸💸</p>
              <p><strong>As a thank you we will also credit you with a free subscription for your first month 🙏</strong></p>
            `;
          }
          this.errorCount++;

          this.$modal.show("dialog", {
            title: "Oh No 🤦",
            text: errorText,
            buttons: [
              {
                title: "Try Again?",
                default: true,
                class: "vue-dialog-button",
                handler: () => {
                  this.$modal.hide("dialog");
                  this.submitMutation();
                },
              },
            ],
          });
        });
    },
  },
};
</script>

<style scoped>
>>> div.v--modal {
  @apply p-0 bg-transparent overflow-visible mt-6;

  @screen md {
    @apply mt-0;
  }

  @screen lg {
    @apply mt-8;
  }
}

div.v--modal-overlay {
  padding-bottom: 150px;

  @screen md {
    padding-bottom: 10%;
  }
}

.step-indicator {
  @apply absolute text-grey-dark text-xs tracking-wide;
  right: 8px;
  top: 8px;
}

.error {
  @apply max-w-sm mx-auto bg-white border-0 shadow-none pt-10;
}

.error-wrap {
  @apply rounded-lg bg-white p-0;
}
</style>

<style>
.onboarding-step {
  @apply bg-white flex flex-wrap;
  border-radius: 10px;
}
</style>
